import { useQuery } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

type InstructorFlagRecord = {
  is_instructor?: boolean | null;
  isInstructor?: boolean | null;
  profile?: InstructorFlagRecord | null;
};

interface RawGetUserResponse {
  user?: InstructorFlagRecord | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readInstructorFlag(value: unknown): boolean | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const record = value as InstructorFlagRecord;

  if (typeof record.is_instructor === 'boolean') {
    return record.is_instructor;
  }

  if (typeof record.isInstructor === 'boolean') {
    return record.isInstructor;
  }

  if (record.profile) {
    return readInstructorFlag(record.profile);
  }

  return undefined;
}

function unwrapGetUserResponse(
  response:
    | RawGetUserResponse
    | LegacyKuduResponseEnvelope<RawGetUserResponse>
    | null
    | undefined,
) {
  if (!response || !isRecord(response)) {
    return null;
  }

  if ('Response' in response) {
    return unwrapGetUserResponse(response.Response as RawGetUserResponse | null);
  }

  return isRecord(response.user) ? (response.user as InstructorFlagRecord) : null;
}

export function getImmediateCourseCreationPermission(
  user: User | null | undefined,
) {
  const userFlag = readInstructorFlag(user);

  if (typeof userFlag === 'boolean') {
    return userFlag;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  return readInstructorFlag(window.__KUDU_E2E_AUTH__);
}

export function useCourseCreationPermission() {
  const { getIdToken, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const immediatePermission = getImmediateCourseCreationPermission(user);
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? undefined;

  const permissionQuery = useQuery<boolean, ApiError>({
    enabled:
      !loading &&
      Boolean(user) &&
      Boolean(kuduUserId) &&
      typeof immediatePermission !== 'boolean',
    queryFn: async () => {
      if (!user || !kuduUserId) {
        throw createDataError('You must be signed in to create a course.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const response = await client.invokeKuduMethod<
        RawGetUserResponse | LegacyKuduResponseEnvelope<RawGetUserResponse>,
        { user_id: string }
      >('GetUser', {
        user_id: kuduUserId,
      });

      return readInstructorFlag(unwrapGetUserResponse(response)) === true;
    },
    queryKey: ['course-creation-permission', kuduUserId],
    retry: false,
  });

  return {
    canCreateCourses:
      typeof immediatePermission === 'boolean'
        ? immediatePermission
        : permissionQuery.data === true,
    isCheckingPermission:
      loading ||
      (Boolean(user) &&
        Boolean(kuduUserId) &&
        typeof immediatePermission !== 'boolean' &&
        permissionQuery.isPending),
    // A check that failed is not a "no": the page says so and can retry,
    // rather than showing an instructor the student's Add Course in silence.
    permissionCheckFailed:
      typeof immediatePermission !== 'boolean' && permissionQuery.isError,
    // Resolves when the check has run again, so the page can keep focus where
    // it was until then.
    retryPermissionCheck: () => permissionQuery.refetch(),
  };
}
